import Autocomplete from '@mui/material/Autocomplete';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { tasksApi } from '../tasks.api';
import type { Meta, TaskDetail } from '../tasks.types';
import { errorMessage, num } from '../tasks.utils';
import DepartmentEmployeeFields from './DepartmentEmployeeFields';
import DialogShell from './DialogShell';
import TaskFieldsBlock, { emptyTaskFields, TaskFieldsValue, taskFieldsBody, taskFieldsProblem } from './TaskFieldsBlock';
import { useCapacityConfirm, wasCancelled } from './useCapacityConfirm';

interface Props {
  open: boolean;
  meta: Meta;
  /** Editing when given; creating otherwise. */
  task?: TaskDetail | null;
  parentTaskId?: number | null;
  defaultProjectId?: number | null;
  onClose: () => void;
  onSaved: (id: number) => void;
}

/**
 * Create or edit a task — the same fields, in the same order, as a task row on the Assign
 * Work screen (both render {@link TaskFieldsBlock} and {@link DepartmentEmployeeFields}).
 * On create it can be assigned in the same step; on edit the owner changes through
 * Assign / Reassign instead.
 */
export default function TaskFormDialog({ open, meta, task, parentTaskId, defaultProjectId, onClose, onSaved }: Props) {
  const editing = Boolean(task);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [requireAcceptance, setRequireAcceptance] = useState(true);
  const [fields, setFields] = useState<TaskFieldsValue>(emptyTaskFields);
  const [busy, setBusy] = useState(false);
  const { run, dialog } = useCapacityConfirm();

  useEffect(() => {
    if (!open) return;
    const t = task?.task;
    setProjectId(t?.projectId ?? defaultProjectId ?? null);
    setDepartmentId(null);
    setAssigneeId(null);
    setRequireAcceptance(true);
    setFields(
      t
        ? {
            title: t.title,
            priority: t.priority ?? 'MEDIUM',
            work: {
              workActivityId: t.workActivityId,
              typeOfWorkActivityId: t.typeOfWorkActivityId,
              structuralWorkType: t.structuralWorkType,
              structuralDrawings: t.structuralDrawings ?? [],
            },
            description: task?.description ?? '',
            startDate: t.startDate ?? '',
            dueDate: t.dueDate ?? '',
            estimatedHours: t.estimatedHours != null ? String(num(t.estimatedHours)) : '',
            allocatedHours: String(num(t.allocatedHours)),
          }
        : emptyTaskFields()
    );
  }, [open, task, defaultProjectId]);

  const changeProject = (id: number | null) => {
    setProjectId(id);
    // Drawings belong to a project.
    setFields((f) => ({ ...f, work: { ...f.work, structuralDrawings: [] } }));
  };

  const submit = async () => {
    if (!projectId) return toast.error('Choose the project.');
    const problem = taskFieldsProblem(fields);
    if (problem) return toast.error(problem);
    setBusy(true);
    try {
      const body = { ...taskFieldsBody(fields, editing), projectId };
      let id: number;
      if (task) {
        id = (await tasksApi.update(task.task.id, body)).task.id;
        toast.success('Task updated');
      } else {
        const created = await run((allowOverCapacity) =>
          tasksApi.create({
            ...body,
            parentTaskId: parentTaskId ?? null,
            assigneeId,
            requireAcceptance,
            allowOverCapacity,
          })
        );
        id = created.task.id;
        toast.success(`${created.task.taskCode} created`);
      }
      onSaved(id);
    } catch (e) {
      if (!wasCancelled(e)) toast.error(errorMessage(e, 'Could not save the task'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogShell
        open={open}
        title={editing ? `EDIT ${task?.task.taskCode}` : parentTaskId ? 'NEW SUBTASK' : 'NEW TASK'}
        onClose={onClose}
        onSubmit={submit}
        submitLabel={editing ? 'Save' : 'Create'}
        busy={busy}
        maxWidth='lg'
      >
        <Autocomplete
          size='small'
          options={meta.projects}
          disabled={Boolean(parentTaskId) || Boolean(task?.task.parentTaskId)}
          value={meta.projects.find((p) => p.id === projectId) ?? null}
          onChange={(_, v) => changeProject(v?.id ?? null)}
          getOptionLabel={(p) => `${p.code} — ${p.name}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          openOnFocus
          autoHighlight
          renderInput={(p) => <TextField {...p} label='Project' required placeholder='Type to search projects' />}
        />
        {!editing && (
          <>
            <DepartmentEmployeeFields
              departments={meta.departments}
              employees={meta.employees}
              departmentId={departmentId}
              onDepartmentChange={setDepartmentId}
              employeeId={assigneeId}
              onEmployeeChange={setAssigneeId}
              employeeLabel='Employee (optional)'
            />
            <FormControlLabel
              disabled={!assigneeId}
              control={<Switch checked={requireAcceptance} onChange={(e) => setRequireAcceptance(e.target.checked)} />}
              label='Employee must accept'
              sx={{ alignSelf: 'flex-start' }}
            />
          </>
        )}
        <TaskFieldsBlock
          value={fields}
          onChange={setFields}
          priorities={meta.priorities}
          activities={meta.workActivities ?? []}
          projectId={projectId}
        />
      </DialogShell>
      {dialog}
    </>
  );
}
